import { Link } from 'react-router-dom';
import { Upsell } from '../../components/plan/Upsell';
import { usePlan } from '../../components/plan/usePlan';
import type { CommunityTemplateRead } from '../../types/api';
import { useSignedAssetUrl } from '../knowledge-base/useSignedAssetUrl';
import {
  COMMUNITY_STATUS_LABEL,
  communityStatusTone,
  isCommunityTemplateEditable,
} from './communityStatus';
import { communityTemplatePath } from './communityTemplatePrefill';
import { CommunityDesignPreview } from './CommunityDesignPreview';
import { NEUTRAL_PALETTE, type BrandPalette } from './communityZones';

export interface YourCommunityTemplatesProps {
  templates: readonly CommunityTemplateRead[];
  isLoading: boolean;
  error: string | null;
  /** Starts the brief on one of these, whatever its status. */
  onUse: (template: CommunityTemplateRead) => void;
  /** The template being handed to the brief, while its design is signed. */
  startingId: string | null;
  palette?: BrandPalette;
  /**
   * False inside a surface that already names the list (the brief's picker has
   * a "Yours" tab), so the section does not repeat its own heading and lede.
   */
  showHeading?: boolean;
}

interface YourTileProps {
  template: CommunityTemplateRead;
  palette: BrandPalette;
  isStarting: boolean;
  disabled: boolean;
  onUse: () => void;
}

/** One own template's tile: the design over its signed upload, if it has one. */
function YourTile({ template, palette, isStarting, disabled, onUse }: YourTileProps) {
  // An own upload is private, so it is signed like any other asset.
  const { url } = useSignedAssetUrl(template.image_path);
  // Posting on an own template outside the gallery is Private templates; an
  // approved one is a community template like any other.
  const { can } = usePlan();
  const canPrivate = can('private_templates');
  const canUse = canPrivate || template.status === 'approved';
  const editable = canPrivate && isCommunityTemplateEditable(template.status);

  return (
    <li className="pack-card">
      <CommunityDesignPreview
        className="design-preview--tile"
        zones={template.zones}
        imageUrl={url}
        palette={palette}
        label={`${template.title} design, in your brand colours`}
      />
      <span className="pack-card__name">{template.title}</span>
      {template.description ? (
        <span className="pack-card__description">{template.description}</span>
      ) : null}
      <span className="pack-card__meta">
        <span className={communityStatusTone(template.status)}>
          {COMMUNITY_STATUS_LABEL[template.status]}
        </span>
      </span>
      <div className="pack-card__actions community-yours__actions">
        <button className="button" type="button" disabled={disabled || !canUse} onClick={onUse}>
          {isStarting ? 'Opening' : 'Use template'}
        </button>
        <Link className="button button--ghost" to={communityTemplatePath(template.id)}>
          {editable ? 'Edit' : 'View'}
        </Link>
      </div>
    </li>
  );
}

/**
 * The business's own community templates, above the shared gallery: every
 * status, because a business may post on its own design the moment it is
 * saved. Review only decides whether everyone else sees it.
 *
 * Renders nothing when there are none; the gallery head's "Create template"
 * is the invitation, and an empty box here would only push the gallery down.
 */
export function YourCommunityTemplates({
  templates,
  isLoading,
  error,
  onUse,
  startingId,
  palette = NEUTRAL_PALETTE,
  showHeading = true,
}: YourCommunityTemplatesProps) {
  const { can } = usePlan();
  if (!isLoading && !error && templates.length === 0) return null;
  // Drafts and templates under review are private uses; say so once, not per tile.
  const hasPrivate = templates.some((template) => template.status !== 'approved');

  return (
    <section
      className="community-yours"
      aria-labelledby={showHeading ? 'community-yours-title' : undefined}
      aria-label={showHeading ? undefined : 'Your templates'}
    >
      {showHeading ? (
        <>
          <h2 className="community-yours__title" id="community-yours-title">
            Yours
          </h2>
          <p className="community-yours__lede">
            Post on any of these now, whatever the status. Review is only for sharing one with every
            member.
          </p>
        </>
      ) : null}

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      {hasPrivate && !can('private_templates') ? <Upsell feature="private_templates" /> : null}

      {isLoading && templates.length === 0 ? (
        <p className="kb-section__status" role="status">
          Loading your templates
        </p>
      ) : (
        <ul className="pack-grid">
          {templates.map((template) => (
            <YourTile
              key={template.id}
              template={template}
              palette={palette}
              isStarting={startingId === template.id}
              disabled={startingId !== null}
              onUse={() => onUse(template)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
